import { motion } from 'framer-motion';
import { Store, ScanBarcode, CreditCard, ShoppingBag } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Store,
    title: 'Enter the Store',
    description: 'Walk into any GrabPe-enabled store. Open the app and tap "Start Shopping" to begin.',
  },
  {
    number: '02',
    icon: ScanBarcode,
    title: 'Scan Products',
    description: 'Point your camera at any product barcode. It instantly gets added to your digital cart with the price.',
  },
  {
    number: '03',
    icon: CreditCard,
    title: 'Pay in the App',
    description: 'When done shopping, review your cart and pay securely with your saved card or digital wallet.',
  },
  {
    number: '04',
    icon: ShoppingBag,
    title: 'Just Walk Out',
    description: 'Show your digital receipt at the exit and leave. No queues, no waiting. Shopping done!',
  },
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-wide uppercase text-brand-primary mb-4 block">
            How it Works
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-secondary mb-6">
            Shopping without{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-accent">
              the queue
            </span>
          </h2>
          <p className="text-lg md:text-xl font-medium leading-relaxed text-neutral-text-secondary max-w-2xl mx-auto">
            Four simple steps to a faster, smarter shopping experience.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-primary via-brand-accent to-brand-primary transform -translate-y-1/2 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
                data-testid={`step-${index + 1}`}
              >
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 text-center h-full">
                  {/* Step Number Badge */}
                  <div className="w-16 h-16 mx-auto bg-brand-primary rounded-2xl flex items-center justify-center mb-6 shadow-[0_4px_14px_0_rgba(99,91,255,0.39)]">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Step Number */}
                  <span className="inline-block text-brand-primary font-heading font-black text-sm mb-3">
                    STEP {step.number}
                  </span>

                  <h3 className="font-heading text-xl font-bold text-brand-secondary mb-3">
                    {step.title}
                  </h3>
                  <p className="text-neutral-text-secondary leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow for mobile */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center my-4 lg:hidden">
                    <div className="w-0.5 h-8 bg-gradient-to-b from-brand-primary to-brand-accent"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Dual CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-16 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8 py-4 font-bold shadow-[0_4px_14px_0_rgba(99,91,255,0.39)] transition-all hover:scale-105 active:scale-95"
            data-testid="how-it-works-shopper-cta"
          >
            Download the App
          </a>
          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-2 bg-brand-secondary hover:bg-brand-secondary/90 text-white rounded-full px-8 py-4 font-bold transition-all hover:scale-105 active:scale-95"
            data-testid="how-it-works-store-cta"
          >
            Get GrabPe for your Store
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
